<template>
    <div>
        <modal id="punch-in-modal"
               size="large"
               v-model="showModal"
               @submit="submit"
               :preloader="preloader"
               :loading="loading || geoLoader"
               :title="!punch ? $t('punch_in') : $t('punch_out')"
               :submit-btn-class="!punch ? 'btn-success' : 'btn-warning'"
               :btn-label="!punch ? $t('punch_in') : $t('punch_out')">
            <div class="d-flex justify-content-between">
                <div v-if="punch" class="d-flex align-items-center mb-4">
                    <div class="icon-box mr-2">
                        <app-icon name="log-out" class="text-warning"/>
                    </div>
                    <div>
                        <p class="text-secondary mb-1">{{ $t('punch_out_date_and_time') }}</p>
                        <h5 class="mb-1">{{ nowDateTime }}</h5>
                        <span class="">{{ nowDate }}</span>
                    </div>
                </div>
                <div class="d-flex align-items-center mb-4">
                    <div class="icon-box mr-2">
                        <app-icon name="log-in" class="text-success"/>
                    </div>
                    <div>
                        <p class="text-secondary mb-1">
                            {{ punch ? $t('punched_in_date_and_time') : $t('punch_in_date_and_time') }}</p>
                        <h5 class="mb-1">{{ punchInDateTime ? punchInDateTime : nowDateTime }}</h5>
                        <span class="">{{ punchInDate ? punchInDate : nowDate }}</span>

                        <div class="d-flex align-items-center justify-content-between">
                            <span class="mr-3">{{$t('work_from_home')}}</span>
                            <app-input
                                type="switch"
                                class="mt-2"
                                :disabled="punch"
                                v-model="geoLocation.work_from_home"
                            />
                        </div>

                    </div>
                </div>
            </div>

            <form class="mb-4"
                  ref="form"
            >
                <app-form-group
                    :label="$t('tags')"
                    type="multi-select"
                    v-model="formData.tag_ids"
                    :list="attendanceTags"
                    list-value-field="title"
                    :error-message="$errorMessage(errors, 'tag_ids')"
                />
                <div v-if="punch" class="mb-3">
                    <label class="d-block mb-2">{{ $t('break_type') }}</label>
                    <app-input
                        type="select"
                        :list="breakTypeList"
                        v-model="breakTimeForm.break_time_id"
                        @input="recalculateBreakEnd"
                        @change="recalculateBreakEnd"
                        :error-message="$errorMessage(errors, 'break_time_id')"
                    />
                    <div class="row mt-3">
                        <div class="col-md-6">
                            <label class="d-block mb-1">{{ $t('break_start') }}</label>
                            <app-input
                                type="time"
                                :placeholder="$t('break_start')"
                                v-model="breakTimeForm.break_start"
                                @input="recalculateBreakEnd"
                                @change="recalculateBreakEnd"
                                :error-message="$errorMessage(errors, 'break_start')"
                            />
                        </div>
                        <div class="col-md-6">
                            <label class="d-block mb-1">{{ $t('break_end') }}</label>
                            <app-input
                                type="time"
                                :placeholder="$t('break_end')"
                                v-model="breakTimeForm.break_end"
                                :error-message="$errorMessage(errors, 'break_end')"
                            />
                        </div>
                    </div>
                </div>
                <label for="description">{{ !punch ? $t('punch_in_note') : $t('punch_out_note') }}</label>
                <app-input
                    id="description"
                    type="textarea"
                    v-model="formData.note"
                    :text-area-rows="4"
                    :placeholder="!punch ? $t('punch_in_note_here') : $t('punch_out_note_here')"
                />
            </form>

            <div id='map' style='width: 100%; height: 300px;'></div>
            <div class="d-flex justify-content-center mb-primary">
                <app-overlay-loader v-if="geoLoader"/>
                <template v-else>
                    <div class="w-100 py-3 px-2"
                         :class="`${geoLocation.error ? 'alert alert-warning text-dark' : 'note note-warning'}`">
                        <p class="m-0" v-if="geoLocation.error">{{ geoLocation.error }}</p>
                        <p class="m-0" v-if="geoLocation.ip && !geoLocation.error"><label
                            class="font-weight-bold">IP:</label>
                            {{ geoLocation.ip }}</p>
                        <p class="m-0 font-xl" v-if="geoLocation.location"><label
                            class="font-weight-bold">Location:</label> {{ geoLocation.location }}</p>
                        <p class="m-0" v-if="geoLocation.city"><label class="font-weight-bold">City:</label>
                            {{ geoLocation.city }}</p>
                        <p class="m-0" v-if="geoLocation.state_prov"><label class="font-weight-bold">State:</label>
                            {{ geoLocation.state_prov }}</p>
                        <p class="m-0" v-if="geoLocation.country_name"><label
                            class="font-weight-bold mb-0">Country:</label> {{ geoLocation.country_name }}</p>
                    </div>
                </template>
            </div>
        </modal>
    </div>
</template>

<script>
import ModalMixin from "../../../../../common/Mixin/Global/ModalMixin";
import FormHelperMixins from "../../../../../common/Mixin/Global/FormHelperMixins";
import {TENANT_BASE_URL} from "../../../../../common/Config/UrlHelper";
import {axiosGet} from "../../../../../common/Helper/AxiosHelper";
import {
    formatDateForServer,
    formatDateToLocal,
    calenderTime,
    onlyTime,
    formatDateTimeToLocalDate,
    formatDateTimeForServer,
    dateTimeToLocalWithFormat
} from "../../../../../common/Helper/Support/DateTimeHelper";
import {ucFirst} from "../../../../../common/Helper/Support/TextHelper";
import {addChooseInSelectArray} from "../../../../../common/Helper/Support/FormHelper";
import mapboxgl from 'mapbox-gl';
import { Loader } from '@googlemaps/js-api-loader';
import moment from "moment";

export default {
    name: "PunchInOutModal",
    props: {
        punch: {}
    },
    data() {
        return {
            formData: {},
            nowDate: '',
            nowDateTime: '',
            punchInDate: '',
            punchInDateTime: '',
            punchInAt: '',
            url: `${TENANT_BASE_URL}employees`,
            preloader: true,
            geoLoader: true,
            geoLocation: {location:"", work_from_home:false},
            map: null,
            interval: null,
            geoSettings: {},
            attendanceTags: [],
            breakTypeList: [],
            breakTimeForm: {
                break_time_id: '',
                break_start: '',
                break_end: ''
            },
            errors: {},
            center: {
                lat: 0,
                lng: 0
            },
        }
    },
    mixins: [ModalMixin, FormHelperMixins],
    mounted() {
        this.nowTimes();
        this.interval = setInterval(this.nowTimes, 20 * 1000);
    },
    created() {
        this.loadAttendanceTags();
        if (this.punch) {
            this.getPunchInTime();
            this.loadBreakTypeList();
            this.url = `${this.url}/punch-out`;
        } else {
            this.getGeolocationSettings();
            this.url = `${this.url}/punch-in`;
            this.preloader = false;
        }
    },
    destroyed() {
        clearInterval(this.interval);
        if (this.map) {
            this.map.remove();
        }
    },
    watch: {
        'breakTimeForm.break_time_id'() {
            this.recalculateBreakEnd();
        },
        'breakTimeForm.break_start'() {
            this.recalculateBreakEnd();
        }
    },
    methods: {
        submit() {
            this.errors = {};
            const breakPayload = this.getBreakPayload();
            this.loading = true;
            this.formData.today = formatDateForServer(new Date());
            this.formData.ip_data = this.geoLocation.error ? {work_from_home: this.geoLocation.work_from_home} : this.geoLocation;
            if (breakPayload) {
                this.formData.break_time_id = breakPayload.break_time_id;
                this.formData.break_start = breakPayload.break_start;
                this.formData.break_end = breakPayload.break_end;
            } else {
                delete this.formData.break_time_id;
                delete this.formData.break_start;
                delete this.formData.break_end;
            }
            this.submitFromFixin(`patch`, this.url, this.formData);
        },
        loadAttendanceTags() {
            axiosGet(`${TENANT_BASE_URL}app/interview-tag?per_page=0&type=4`).then(({data}) => {
                this.attendanceTags = data;
            });
        },
        afterSuccess({data}) {
            this.$toastr.s('', data.message);
            this.breakTimeForm = {
                break_time_id: '',
                break_start: '',
                break_end: ''
            };
            this.reloadPunchInOut();
        },
        afterError({data}) {
            const hasBreakInput = !!(
                this.breakTimeForm.break_time_id ||
                this.breakTimeForm.break_start ||
                this.breakTimeForm.break_end
            );
            if (data?.errors) {
                this.errors = data.errors;
            }
            if (hasBreakInput && !data?.errors && data?.message) {
                if (data.message === this.$t('break_start_must_be_after_punch_in')) {
                    this.$set(this.errors, 'break_start', [data.message]);
                } else if (data.message === this.$t('break_end_must_be_before_punch_out')) {
                    this.$set(this.errors, 'break_end', [data.message]);
                } else if (data.message === this.$t('break_start_must_be_before_end')) {
                    this.$set(this.errors, 'break_end', [data.message]);
                }
            }
            const message = data?.errors?.out_time?.[0]
                || data?.errors?.in_time?.[0]
                || data?.errors?.break_start?.[0]
                || data?.errors?.break_end?.[0]
                || data?.errors?.break_time_id?.[0]
                || data?.message;
            if (!hasBreakInput || !Object.keys(this.errors).length) {
                this.$toastr.e('', message);
            }
            if (!hasBreakInput) {
                this.reloadPunchInOut();
            }
        },
        dateNameWithTime(date) {
            let dateName = calenderTime(date, false, true);
            let time = onlyTime(date);
            return `${ucFirst(dateName)} (${time})`;
        },
        nowTimes() {
            this.nowDate = formatDateToLocal(new Date());
            this.nowDateTime = this.dateNameWithTime(new Date());
        },
        getPunchInTime() {
            axiosGet(`${this.url}/punch-in-time`).then(({data}) => {
                this.punchInAt = data.in_time;
                this.punchInDate = formatDateTimeToLocalDate(data.in_time);
                this.punchInDateTime = this.dateNameWithTime(data.in_time);
                this.preloader = false;
                this.checkPunchInFromHome(data.in_ip_data);
                this.getGeolocationSettings();
                // Load previously selected tags
                if (data.tag_ids && data.tag_ids.length) {
                    this.formData.tag_ids = data.tag_ids;
                }
            }).catch(({response}) => {
                this.$toastr.e('', response.data?.errors?.in_time[0]);
                setTimeout(() => location.reload())
            })
        },
        loadBreakTypeList() {
            axiosGet(`${TENANT_BASE_URL}selectable/break-times`)
                .then(response => {
                    const list = response.data.map(bt => ({
                        id: bt.id,
                        value: bt.name,
                        duration: bt.duration
                    }));
                    this.breakTypeList = addChooseInSelectArray(list, 'value', 'break_type');
                    this.breakTimeForm.break_time_id = '';
                });
        },
        recalculateBreakEnd() {
            this.$nextTick(() => {
                const {break_time_id, break_start} = this.breakTimeForm;
                if (!break_time_id || !break_start) {
                    return;
                }

                const breakType = this.breakTypeList.find(b => String(b.id) === String(break_time_id));
                if (!breakType?.duration) {
                    return;
                }

                const durationParts = String(breakType.duration).split(':');
                const hours = parseInt(durationParts[0], 10) || 0;
                const minutes = parseInt(durationParts[1], 10) || 0;

                const start = moment(break_start, ['HH:mm', 'h:mm A', 'hh:mm A', 'hh:mm a']);
                if (!start.isValid()) {
                    return;
                }

                const end = start.clone().add(hours, 'hours').add(minutes, 'minutes');
                const timeFormat = break_start.match(/am|pm/i) ? 'hh:mm A' : 'HH:mm';
                this.breakTimeForm.break_end = end.format(timeFormat);
            });
        },
        getBreakPayload() {
            if (!this.punch) {
                return null;
            }

            const {break_time_id, break_start, break_end} = this.breakTimeForm;
            const hasAny = !!(break_time_id || break_start || break_end);

            if (!hasAny) {
                return null;
            }

            const inferredBreakStart = break_start ? this.inferBreakDateTime(break_start) : break_start;

            return {
                break_time_id,
                break_start: inferredBreakStart,
                break_end: break_end ? this.inferBreakDateTime(break_end, {notBefore: inferredBreakStart}) : break_end
            };
        },
        inferBreakDateTime(time, {notBefore = null} = {}) {
            const parsedTime = moment(time, ['HH:mm:ss', 'HH:mm', 'H:mm', 'hh:mm A', 'h:mm A'], true);

            if (!parsedTime.isValid()) {
                return time;
            }

            const attendanceStart = this.punchInAt
                ? moment(dateTimeToLocalWithFormat(this.punchInAt), 'YYYY-MM-DD HH:mm:ss')
                : moment();
            const attendanceEnd = moment();
            const referenceStart = notBefore
                ? moment(dateTimeToLocalWithFormat(notBefore), 'YYYY-MM-DD HH:mm:ss')
                : null;
            const candidateDates = [attendanceStart.clone().startOf('day')];

            if (!attendanceEnd.isSame(attendanceStart, 'day')) {
                candidateDates.push(attendanceEnd.clone().startOf('day'));
            }

            const candidates = candidateDates
                .map(date => date.clone()
                    .hour(parsedTime.hour())
                    .minute(parsedTime.minute())
                    .second(parsedTime.second())
                    .millisecond(0))
                .filter(candidate => {
                    if (candidate.isBefore(attendanceStart) || candidate.isAfter(attendanceEnd)) {
                        return false;
                    }

                    if (referenceStart && candidate.isBefore(referenceStart)) {
                        return false;
                    }

                    return true;
                })
                .sort((first, second) => first.valueOf() - second.valueOf());

            const selected = candidates[0];

            if (!selected) {
                const fallbackDate = attendanceStart.clone().startOf('day');
                const fallback = fallbackDate
                    .hour(parsedTime.hour())
                    .minute(parsedTime.minute())
                    .second(parsedTime.second())
                    .millisecond(0);

                if (referenceStart && fallback.isBefore(referenceStart)) {
                    fallback.add(1, 'day');
                }

                return formatDateTimeForServer(fallback.format('YYYY-MM-DD HH:mm:ss'));
            }

            return formatDateTimeForServer(selected.format('YYYY-MM-DD HH:mm:ss'));
        },
        reloadPunchInOut() {
            this.$hub.$emit('reload-punch-in-out-button');
            this.$hub.$emit('reload-dashboard');
            $("#punch-in-modal").modal('hide');
        },
        checkPunchInFromHome(punchInDetails){
            punchInDetails =  punchInDetails ? JSON.parse(punchInDetails) : {};
            if(punchInDetails.work_from_home){
                this.geoLocation.work_from_home=true;
            }
        },
        getGeolocationSettings() {
            axiosGet(this.apiUrl.GEOLOCATION).then(({data}) => {
                this.geoSettings = data;
                if (data.service_name && !['none', 'distance_limit'].includes(data.service_name)) {
                    if (data.service_name === 'mapbox') {
                        this.initializeMap(data.api_key);
                    } else if (data.service_name === 'google_map') {
                        // this.getGeoLocationData(data.ip_endpoint);
                        this.initializeGoogleMap(data.api_key);
                    } else if (data.service_name === 'ip_geolocation') {
                        document.querySelector('#map').style.display = 'none'
                        this.getGeoLocationData(`${data.api_endpoint}${data.api_key}`);
                    }
                } else {
                    document.querySelector('#map').style.display = 'none'
                    this.getGeoLocationData(data.ip_endpoint);
                }
            }).catch(({response}) => {
                document.querySelector('#map').style.display = 'none'
                this.geoLocation.error = 'Can not fetch IP data.';
                console.log(response)
                this.geoLoader = false;
            });
        },
        getGeoLocationData(url) {
            fetch(url).then((response) => {
                if (response.ok) {
                    return response.json()
                }
                if (response.status == 401) {
                    throw new Error('Provided Geolocation API KEY is not valid or expired.');
                }
                throw new Error('Can not fetch IP data.');
            }).then((response) => {
                this.geoLocation = {...this.geoLocation, ...response};
            }).catch((error) => {
                this.geoLocation.error = error;
            }).finally(() => this.geoLoader = false);
        },
        initializeMap(token) {
            mapboxgl.accessToken = token;
            this.map = new mapboxgl.Map({
                container: 'map', // container ID
                style: 'mapbox://styles/mapbox/streets-v11', // style URL
                language: window.appLanguage
            });
            this.identifyLocation(token)
        },
        initializeGoogleMap(token) {
            this.getCurrentPositionByNavigator()
                .then(coords => {
                    this.center = coords;
                    this.geoLocation.coordinate = coords;
                    this.renderGoogleMap(token);
                    this.getGoogleMapGeoLocationData(token, this.center.lat, this.center.lng);
                })
                .catch(err => {
                    console.log(err.message)
                    this.geoLocation.error = 'Provided Geolocation API KEY is not valid or expired.';
                    this.geoLoader = false
                })
        },
        getCurrentPositionByNavigator() {
            return new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const coords = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        };
                        resolve(coords);
                    },
                    (error) => {
                        reject(error);
                    }
                );
            });
        },
        renderGoogleMap(token) {
            const loader = new Loader({
                apiKey: token,
                version: "weekly",
                language: window.appLanguage,
                libraries: ["places"]
            });
            const mapOptions = {
                center: this.center,
                zoom: 16,
                gestureHandling: "cooperative",
            };
            loader
                .load()
                .then((google) => {
                    const map = new google.maps.Map(document.getElementById("map"), mapOptions);
                    new google.maps.Marker({
                        position: this.center,
                        map: map,
                    });
                })
                .catch(err => {
                    console.log(err)
                    this.geoLoader = false;
                });
        },
        identifyLocation(token) {
            // Initialize the geolocate control.
            const geolocate = new mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true,
                },
                trackUserLocation: true,
            });

            // Add the control to the map.
            this.map.addControl(geolocate);
            // Show the location on map.
            this.map.on('load', () => {
                geolocate.trigger();
            });

            geolocate.options.geolocation.getCurrentPosition(
                (pos) => {
                    const crd = pos.coords;
                    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${crd.longitude},${crd.latitude}.json?limit=1&types=locality&language=${window.appLanguage}&access_token=${token}`
                    console.log(`Latitude : ${crd.latitude}`);
                    console.log(`Longitude: ${crd.longitude}`);
                    console.log(`More or less ${crd.accuracy} meters.`);
                    this.geoLocation.coordinate = {lat: crd.latitude, lng: crd.longitude};
                    this.getMapboxGeoLocationData(url)
                },
                (err) => {
                    console.warn(`ERROR(${err.code}): ${err.message}`);
                    document.querySelector('#map').style.display = 'none'
                    this.geoLocation.error = `ERROR: Only secure origins are allowed to access the map.`;
                    this.geoLoader = false
                }
            )
        },
        getGoogleMapGeoLocationData(token, lat, lng) {
            let url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${token}&language=${window.appLanguage}`;
            fetch(url).then((response) => {
                if (response.ok) {
                    return response.json()
                }
                if (response.status == 401) {
                    throw new Error('Provided API KEY is not valid or expired.');
                }
                throw new Error('Can not fetch data.');
            }).then(({ results }) => {
                if (results.length) {
                    this.geoLocation.location = results[0].formatted_address;
                }
                this.getGeoLocationData(this.geoSettings.ip_endpoint);
            }).catch((error) => {
                this.geoLocation.error = error;
                this.geoLoader = false
            });
        },
        getMapboxGeoLocationData(url) {
            fetch(url).then((response) => {
                if (response.ok) {
                    return response.json()
                }
                if (response.status == 401) {
                    document.querySelector('#map').style.display = 'none'
                    throw new Error('Provided API KEY is not valid or expired.');
                }
                throw new Error('Can not fetch data.');
            }).then(({features}) => {
                if (features.length) {
                    this.geoLocation.location = features[0].place_name_en || features[0].place_name;
                }
                this.getGeoLocationData(this.geoSettings.ip_endpoint);
            }).catch((error) => {
                document.querySelector('#map').style.display = 'none'
                this.geoLocation.error = error;
                this.geoLoader = false
            });
        }
    }
}
</script>

<style scoped lang="scss">
.icon-box {
    width: 90px;
    height: 90px;

    svg {
        width: 26px;
        height: 26px;
    }
}
</style>
